import platform from '@play-co/gcinstant';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PetSpecies,
  PetStatusEffect,
} from 'src/replicant/buruburu/pet/pet.ruleset';
import { RecordOfAllBut, WithId } from 'src/replicant/utils/types';
import { AnimationState } from '../appearance/appearance.config';
import { State } from 'src/replicant/State';
import { syncPetState } from './pet.util';

export type Attributes = {
  name: string;
  isUpdating: boolean;
  age: number;
  love: number;
  stomach: number;
  dirtiness: number;
  urine: number;
  toothAcidity: number;
};

export type PetState = {
  species: PetSpecies;
  statusEffects: Partial<Record<PetStatusEffect, boolean>>;
  action: AnimationState;
  attributes: Attributes;
};
export const initialAttributes: Attributes = {
  name: '',
  isUpdating: false,
  age: 0,
  love: 0,
  stomach: 0,
  dirtiness: 0,
  urine: 0,
  toothAcidity: 0,
};

const initialPetState: PetState = {
  species: null,
  statusEffects: {},
  attributes: initialAttributes,
  action: null,
};

const initialState = {} as RecordOfAllBut<
  string,
  typeof initialPetState,
  keyof typeof initialPetState
>;

const pet = createSlice({
  name: 'pet',

  initialState,

  reducers: {
    createPet: (
      state,
      { payload }: PayloadAction<WithId<Partial<PetState>>>,
    ) => {
      state[payload.id] = {
        ...initialPetState,
        ...(payload.payload ?? {}),
      };
    },

    setIsUpdating(state, { payload }: PayloadAction<boolean>) {
      if (!state[platform.playerID]) {
        return;
      }

      state[platform.playerID].attributes.isUpdating = payload;
    },

    setAttributes: (
      state,
      { payload: { id, payload } }: PayloadAction<WithId<Partial<Attributes>>>,
    ) => {
      if (!state[id]) {
        return;
      }

      Object.entries(payload).forEach(([item, amount]) => {
        state[id].attributes[item] = amount;
      });
    },

    setSpecies(
      pet,
      { payload: { payload, id } }: PayloadAction<WithId<PetSpecies>>,
    ) {
      pet[id].species = payload;
    },

    setStatusEffects(
      pet,
      {
        payload: { payload, id },
      }: PayloadAction<WithId<PetState['statusEffects']>>,
    ) {
      Object.assign(pet[id].statusEffects, payload);
    },

    setAction(
      pet,
      { payload: { payload, id } }: PayloadAction<WithId<AnimationState>>,
    ) {
      pet[id].action = payload;
    },

    deletePet(pet, { payload }: PayloadAction<string>) {
      delete pet[payload];
    },
  },

  extraReducers: {
    RESET_APP: (_, { payload }: PayloadAction<State>) => {
      return {
        [platform.playerID]: syncPetState(initialPetState, payload),
      };
    },
  },
});

export const petActions = pet.actions;
export default pet.reducer;
